import React, { useRef } from "react";
import { Gauge, AlertTriangle, CheckCircle2, Minus, Plus, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { ccCard } from "../care/ui";
import { CLASSROOM_RULES, STAFF_CREDENTIALS } from "./opsData";

// Real-time child-to-staff ratio engine with automated anomaly alerts
export default function Compliance({ ctx }) {
  const [rooms, setRooms] = React.useState(CLASSROOM_RULES);
  const violating = useRef({});

  const ratioOf = (r) => (r.staff > 0 ? r.children / r.staff : Infinity);
  const isOver = (r) => ratioOf(r) > r.ratioLimit;

  const adjust = (id, field, delta) => {
    setRooms((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, [field]: Math.max(0, r[field] + delta) };
        const over = next.staff > 0 ? next.children / next.staff > next.ratioLimit : next.children > 0;
        const was = violating.current[id];
        if (over && !was) {
          violating.current[id] = true;
          const msg = `RATIO ALERT: ${next.name} at ${(next.staff > 0 ? next.children / next.staff : next.children).toFixed(1)}:1 (limit 1:${next.ratioLimit})`;
          ctx.addLog("UPDATE", "attendance_logs", msg);
          ctx.onAlert?.({ type: "danger", message: msg });
          toast.error(msg);
        } else if (!over && was) {
          violating.current[id] = false;
          ctx.addLog("UPDATE", "attendance_logs", `${next.name} back within ratio compliance`);
          toast.success(`${next.name} back within ratio.`);
        }
        return next;
      })
    );
  };

  return (
    <div className="space-y-6" data-testid="compliance-engine">
      {/* Ratio engine */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h4 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2">
            <Gauge className="w-4 h-4 text-[#5CB3DC]" /> Real-Time Ratio Tracking
          </h4>
          <p className="text-[10px] text-[#9CA3AF] font-medium">Checked-in children vs clocked-in staff · state limits enforced live</p>
        </div>
        <div className="space-y-3">
          {rooms.map((r) => {
            const over = isOver(r);
            const ratio = r.staff > 0 ? (r.children / r.staff).toFixed(1) : "—";
            return (
              <div
                key={r.id}
                data-testid={`room-${r.id}`}
                className={`p-4 rounded-2xl border transition-colors ${
                  over
                    ? "bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:border-rose-800"
                    : "bg-[#FFF9F2] border-[#E6F2FA] dark:bg-[#273244] dark:border-[#374457]"
                }`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100">{r.name}</p>
                    <p className="text-[11px] text-[#9CA3AF]">
                      State limit 1:{r.ratioLimit} · Current{" "}
                      <span className={`font-bold ${over ? "text-rose-500" : "text-[#2F6031]"}`} data-testid={`room-ratio-${r.id}`}>
                        {r.staff > 0 ? `${ratio}:1` : "No staff clocked in"}
                      </span>
                    </p>
                  </div>
                  {over ? (
                    <span className="px-3 py-1.5 bg-rose-100 border border-rose-300 text-rose-600 text-[10px] font-extrabold uppercase rounded-full flex items-center gap-1" data-testid={`room-status-${r.id}`}>
                      <AlertTriangle className="w-3.5 h-3.5" /> Over Ratio
                    </span>
                  ) : (
                    <span className="px-3 py-1.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] text-[10px] font-extrabold uppercase rounded-full flex items-center gap-1" data-testid={`room-status-${r.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Compliant
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {[
                    { label: "Children checked in", field: "children", val: r.children },
                    { label: "Staff clocked in", field: "staff", val: r.staff },
                  ].map((g) => (
                    <div key={g.field} className="flex items-center justify-between bg-white border border-[#FCE6E9] rounded-xl px-3 py-2 dark:bg-[#232D3D] dark:border-[#374457]">
                      <span className="text-[11px] font-bold text-[#9CA3AF]">{g.label}</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => adjust(r.id, g.field, -1)}
                          data-testid={`room-${r.id}-${g.field}-minus`}
                          className="w-6 h-6 rounded-full bg-[#FCE6E9] text-[#B2607F] flex items-center justify-center hover:bg-[#F8C8DC] transition-colors"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm font-black font-heading w-6 text-center text-[#374151] dark:text-slate-100" data-testid={`room-${r.id}-${g.field}-count`}>
                          {g.val}
                        </span>
                        <button
                          onClick={() => adjust(r.id, g.field, 1)}
                          data-testid={`room-${r.id}-${g.field}-plus`}
                          className="w-6 h-6 rounded-full bg-[#E6F2FA] text-[#3D7E9E] flex items-center justify-center hover:bg-[#B3E0F2] transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Credential expiry watch */}
      <div>
        <h4 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2 mb-3">
          <BadgeCheck className="w-4 h-4 text-[#E5A0BC]" /> Staff Credential Expiry Watch
        </h4>
        <div className="space-y-2">
          {STAFF_CREDENTIALS.map((c) => {
            const warning = c.daysLeft <= 30;
            return (
              <div key={c.id} className="p-3.5 bg-white border border-[#FCE6E9] rounded-2xl flex items-center justify-between gap-3 flex-wrap dark:bg-[#232D3D] dark:border-[#374457]" data-testid={`cred-row-${c.id}`}>
                <div>
                  <p className="text-xs font-extrabold text-[#374151] dark:text-slate-100">
                    {c.staff} · <span className="font-semibold text-[#6B7280] dark:text-slate-300">{c.credential}</span>
                  </p>
                  <p className="text-[11px] text-[#9CA3AF]">Expires {c.expiresAt}</p>
                </div>
                <span
                  className={`px-3 py-1 border text-[10px] font-extrabold uppercase rounded-full ${
                    warning ? "bg-[#FFF2D1] border-[#F5E3B3] text-[#7A5A17]" : "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]"
                  }`}
                  data-testid={`cred-days-${c.id}`}
                >
                  {warning ? `${c.daysLeft} days left` : "Current"}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-[#9CA3AF] font-medium mt-3">
          Automated anomaly alerts fire via dashboard notification, SMS and email dispatch log when ratios break limits or credentials near expiry.
        </p>
      </div>
    </div>
  );
}
